import React from 'react';
import { Scale, Phone, Globe, ClipboardCheck, Gavel, Building, FileSignature, Home, Users, Stamp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const ServicesPage = () => {
  const services = [
    {
      id: 'visa',
      icon: <Globe className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Visa & Immigration',
      description: 'Navigating Thai visa requirements can be complex. We provide complete visa services for expats, tourists, and long-term residents.',
      details: [
        'Tourist visa extensions',
        'Retirement visa applications',
        'Marriage visa processing',
        'Education visa support',
        'Work visa coordination'
      ],
      whoItsFor: 'Expats, tourists, retirees, and foreign residents planning to stay in Thailand',
      outcome: 'Smooth visa processing with minimal delays and full compliance with Thai immigration law'
    },
    {
      id: 'work-permits',
      icon: <ClipboardCheck className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Work Permits',
      description: 'Professional assistance with work permit applications, renewals, and compliance for foreign workers in Thailand.',
      details: [
        'Work permit applications',
        'Work permit renewals',
        'Employer compliance guidance',
        'Document preparation',
        'Labour Department liaison'
      ],
      whoItsFor: 'Foreign professionals and businesses employing foreign staff',
      outcome: 'Legal authorisation to work in Thailand with full regulatory compliance'
    },
    {
      id: 'company',
      icon: <Building className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Company Registration',
      description: 'Full business formation and company registration services to help you establish your business in Thailand.',
      details: [
        'Thai limited company formation',
        'Foreign business licence applications',
        'BOI promotion support',
        'Shareholder agreements',
        'Business structure planning'
      ],
      whoItsFor: 'Entrepreneurs and businesses seeking to establish operations in Thailand',
      outcome: 'Fully registered business entity ready to operate legally in Thailand'
    },
    {
      id: 'will',
      icon: <FileSignature className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Will and Testament',
      description: 'Secure your legacy with professionally drafted wills and comprehensive estate planning services.',
      details: [
        'Will drafting and registration',
        'Estate planning',
        'Inheritance law guidance',
        'International will recognition',
        'Trust establishment'
      ],
      whoItsFor: 'Anyone with assets in Thailand who wants to ensure proper distribution to beneficiaries',
      outcome: 'Legally binding will that protects your assets and provides peace of mind'
    },
    {
      id: 'property',
      icon: <Home className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Thai Property Law',
      description: 'Navigate property investment and ownership with expert legal guidance on Thai property regulations.',
      details: [
        'Property purchase agreements',
        'Title deed verification',
        'Condominium foreign quota checks',
        'Leasehold agreements',
        'Property dispute resolution'
      ],
      whoItsFor: 'Property buyers, sellers, and investors in the Thai real estate market',
      outcome: 'Secure property transactions with full legal protection and clear ownership'
    },
    {
      id: 'family',
      icon: <Users className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Divorce & Family Law',
      description: 'Compassionate support for divorce, child custody, and family legal matters during difficult times.',
      details: [
        'Uncontested divorce proceedings',
        'Contested divorce representation',
        'Child custody arrangements',
        'Spousal maintenance',
        'Property division'
      ],
      whoItsFor: 'Couples and families facing separation or custody issues',
      outcome: 'Fair resolution of family matters with minimal emotional stress'
    },
    {
      id: 'civil',
      icon: <Scale className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Civil Law & Litigation',
      description: 'Professional representation for civil disputes, contract issues, and litigation cases.',
      details: [
        'Contract disputes',
        'Debt recovery',
        'Personal injury claims',
        'Civil court representation',
        'Alternative dispute resolution'
      ],
      whoItsFor: 'Individuals and businesses involved in civil legal disputes',
      outcome: 'Professional advocacy to protect your rights and achieve favourable outcomes'
    },
    {
      id: 'notarial',
      icon: <Stamp className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Notarial Services / Attorney',
      description: 'Certified notarial services and powers of attorney for official document authentication.',
      details: [
        'Document notarisation',
        'Power of attorney preparation',
        'Statutory declarations',
        'Document translation verification',
        'Embassy liaison services'
      ],
      whoItsFor: 'Anyone requiring certified legal documents for official or international use',
      outcome: 'Legally certified documents recognised by Thai and international authorities'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Legal Services Pattaya | Immigration Lawyer, Property Lawyer, Company Registration"
        description="Pattaya Solicitors legal services: Immigration lawyer Pattaya Thailand, property lawyer for foreign buyers, company registration lawyer, divorce attorney for expats. English speaking law firm."
        keywords="Immigration Lawyer Pattaya Thailand, Property Lawyer Pattaya for Foreign Buyers, Company Registration Lawyer Pattaya, Divorce Attorney Pattaya for Expats, Pattaya Legal Services, English Speaking Lawyer Pattaya"
        path="/services"
        schemaType="services"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm">
              <Scale className="w-4 h-4 text-[#D4A74A]" />
              Our Services
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">Legal Services for Expats & Foreigners in Pattaya</h1>
            <p className="text-xl text-gray-300">Immigration Lawyer | Property Lawyer | Company Registration | Divorce Attorney</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-700 leading-relaxed">
              At Pattaya Solicitors, we offer a wide range of legal services tailored to meet the needs of both domestic and foreign clients. Our experienced team is here to guide you through every step of the legal process.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detailed */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-16">
            {services.map((service, index) => (
              <Card 
                key={service.id}
                id={service.id}
                className="border-2 hover:border-[#D4A74A] transition-all duration-300 shadow-lg scroll-mt-24"
              >
                <CardContent className="p-8 lg:p-12">
                  <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-2">
                      <div className="w-20 h-20 bg-[#F5E7D2] rounded-full flex items-center justify-center">
                        {service.icon}
                      </div>
                    </div>
                    
                    <div className="lg:col-span-10 space-y-6">
                      <div>
                        <h2 className="text-3xl font-bold text-[#000A22] mb-3">{service.title}</h2>
                        <p className="text-lg text-gray-700">{service.description}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-[#000A22] mb-3">What We Offer:</h3>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {service.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <span className="text-[#D4A74A] mt-1">✓</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-[#000A22] mb-2">Who It's For:</h4>
                          <p className="text-gray-700">{service.whoItsFor}</p>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#000A22] mb-2">Typical Outcome:</h4>
                          <p className="text-gray-700">{service.outcome}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 pt-4">
                        <Button 
                          className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold"
                          asChild
                        >
                          <a href="tel:+6638412415">
                            <Phone className="w-4 h-4 mr-2" />
                            Call Now
                          </a>
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-2 border-[#000A22] text-[#000A22] hover:bg-[#000A22] hover:text-white font-semibold"
                          asChild
                        >
                          <a href="/contact">Chat With Us</a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#000A22] to-[#001438] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">Need Legal Assistance?</h2>
            <p className="text-xl text-gray-300">
              Contact us today to discuss your legal needs with our experienced team.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                asChild
              >
                <a href="tel:+6638412415">
                  <Phone className="w-5 h-5 mr-2" />
                  +66 (38) 412-415
                </a>
              </Button>
              
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                asChild
              >
                <a href="tel:+66818383869">
                  <Phone className="w-5 h-5 mr-2" />
                  +66 (81) 838-3869
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
